import { useOrders } from '@/hooks/useOrders';
import { OrderCard } from '@/components/orders/OrderCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, FileText, Truck, CheckCircle, XCircle, Clock, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const MyOrders = () => {
  const { orders, isLoading } = useOrders();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Group orders by status
  const draftOrders = orders.filter(o => o.status === 'draft');
  const postedOrders = orders.filter(o => o.status === 'posted');
  const bookedOrders = orders.filter(o => o.status === 'booked');
  const assignedOrders = orders.filter(o => o.status === 'assigned');
  const inTransitOrders = orders.filter(o => o.status === 'in_transit');
  const deliveredOrders = orders.filter(o => o.status === 'delivered');
  const cancelledOrders = orders.filter(o => o.status === 'cancelled');

  const statusTabs = [
    { value: 'all', label: t('orders.statuses.all'), count: orders.length, icon: Package },
    { value: 'draft', label: t('orders.statuses.draft'), count: draftOrders.length, icon: FileText },
    { value: 'posted', label: t('orders.statuses.posted'), count: postedOrders.length, icon: Clock },
    { value: 'booked', label: t('orders.statuses.booked'), count: bookedOrders.length, icon: CheckCircle },
    { value: 'assigned', label: t('orders.statuses.assigned'), count: assignedOrders.length, icon: Truck },
    { value: 'in_transit', label: t('orders.statuses.in_transit'), count: inTransitOrders.length, icon: Navigation },
    { value: 'delivered', label: t('orders.statuses.delivered'), count: deliveredOrders.length, icon: CheckCircle },
    { value: 'cancelled', label: t('orders.statuses.cancelled'), count: cancelledOrders.length, icon: XCircle },
  ];

  const renderOrdersList = (ordersList: typeof orders, emptyMessage: string) => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      );
    }

    if (ordersList.length === 0) {
      return (
        <div className="text-center py-12">
          <Package className="mx-auto text-border-muted mb-4" size={48} />
          <p className="text-sm text-muted-foreground mb-4">{emptyMessage}</p>
          <Button 
            onClick={() => navigate('/shipper/orders/create')}
            className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
          >
            {t('orders.create')}
          </Button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ordersList.map((order) => (
          <OrderCard key={order.id} order={order} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#37699E] font-heading">
            {t('orders.title')}
          </h1>
          <p className="text-sm text-[#5A6B7A] font-body mt-2">
            {t('orders.subtitle')}
          </p>
        </div>
        <Button 
          onClick={() => navigate('/shipper/orders/create')}
          className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
        >
          {t('orders.createNew')}
        </Button>
      </div>

      <Card className="bg-white rounded-xl shadow-md border border-[#DBE2EA]">
        <CardHeader>
          <CardTitle className="text-xl font-medium text-[#37699E] font-heading">
            {t('orders.filterByStatus')}
          </CardTitle>
          <CardDescription className="text-sm text-[#5A6B7A] font-body">
            {t('orders.filterDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-full md:w-[300px] mb-6 border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
              <SelectValue placeholder={t('orders.selectStatus')} />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              {statusTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <SelectItem 
                    key={tab.value} 
                    value={tab.value}
                    className="cursor-pointer hover:bg-[#EBF4F8] focus:bg-[#EBF4F8]"
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={16} className="text-[#0880AE]" />
                      <span className="font-medium text-[#2D3748]">{tab.label}</span>
                      <span className="ml-auto px-2 py-0.5 rounded-full bg-[#EBF4F8] text-xs font-semibold text-[#0880AE]">
                        {tab.count}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <div className="mt-6">
            {selectedStatus === 'all' && renderOrdersList(orders, t('orders.emptyStates.all'))}
            {selectedStatus === 'draft' && renderOrdersList(draftOrders, t('orders.emptyStates.draft'))}
            {selectedStatus === 'posted' && renderOrdersList(postedOrders, t('orders.emptyStates.posted'))}
            {selectedStatus === 'booked' && renderOrdersList(bookedOrders, t('orders.emptyStates.booked'))}
            {selectedStatus === 'assigned' && renderOrdersList(assignedOrders, t('orders.emptyStates.assigned'))}
            {selectedStatus === 'in_transit' && renderOrdersList(inTransitOrders, t('orders.emptyStates.in_transit'))}
            {selectedStatus === 'delivered' && renderOrdersList(deliveredOrders, t('orders.emptyStates.delivered'))}
            {selectedStatus === 'cancelled' && renderOrdersList(cancelledOrders, t('orders.emptyStates.cancelled'))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyOrders;
